import { useState, useEffect, useRef } from 'react';

const PROBE_URL = '/robots.txt';
const RECHECK_INTERVAL_MS = 5000;

// Vérifie une vraie connectivité réseau en tapant une petite ressource
// same-origin. On ne se fie PAS à navigator.onLine : sur certains setups
// (proxy, VPN, config réseau) il reste bloqué sur "false" alors que la
// connexion fonctionne, ce qui affichait le bandeau à tort (faux positif).
const checkConnectivity = async () => {
  try {
    const response = await fetch(`${PROBE_URL}?_=${Date.now()}`, {
      method: 'HEAD',
      cache: 'no-store',
    });
    return response.ok;
  } catch {
    return false;
  }
};

const OfflineBanner = () => {
  const [isOffline, setIsOffline] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const clearRecheck = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    // Re-test périodique déclenché UNIQUEMENT pendant une coupure, pour masquer
    // le bandeau dès le retour de la connexion. En fonctionnement normal (en
    // ligne), aucune requête de fond n'est émise : la navigation n'est pas alourdie.
    const startRecheck = () => {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(async () => {
        if ((await checkConnectivity()) && !cancelled) {
          setIsOffline(false);
          clearRecheck();
        }
      }, RECHECK_INTERVAL_MS);
    };

    const evaluate = async () => {
      const online = await checkConnectivity();
      if (cancelled) return;
      setIsOffline(!online);
      if (online) {
        clearRecheck();
      } else {
        startRecheck();
      }
    };

    const handleOnline = () => {
      setIsOffline(false);
      clearRecheck();
    };
    const handleOffline = () => {
      void evaluate();
    };
    // Au retour sur l'onglet, on re-teste (gratuit, piloté par événement) :
    // rattrape une coupure survenue pendant que l'onglet était en arrière-plan.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        void evaluate();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibility);

    // Vérification initiale (une seule requête au montage) : corrige le faux
    // positif quand navigator.onLine est bloqué sur false alors qu'on est en ligne.
    void evaluate();

    return () => {
      cancelled = true;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibility);
      clearRecheck();
    };
  }, []);

  if (!isOffline) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[110] bg-warning text-warning-foreground text-center text-sm font-medium py-2! px-6" style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}>
      Vous êtes hors ligne
    </div>
  );
};

export default OfflineBanner;
